import { KnowledgeArticle, createArticle } from '../types'

export const risksArticles: KnowledgeArticle[] = [
  createArticle({
    id: 'risk-register-overview',
    title: 'Risk Register Overview',
    section: 'risks',
    summary: 'The risk register tracks organizational risks with likelihood, impact scores, and treatment plans. Link risks to controls and monitor residual risk.',
    content: `
# Risk Register Overview

The Risk Register is a centralized repository for identifying, assessing, and tracking risks across your organization.

## What Is a Risk Register?

A risk register documents:
- Identified risks to your organization
- Assessment of likelihood and impact
- Risk treatment decisions
- Mitigation controls and status
- Risk ownership and accountability

## Risk Properties

### Identification
- **Title** - Brief name for the risk
- **Description** - Detailed explanation
- **Category** - Risk domain (operational, security, compliance, etc.)
- **Source** - How the risk was identified

### Assessment
- **Likelihood** - Probability of occurrence (1-5 scale)
- **Impact** - Severity if it occurs (1-5 scale)
- **Inherent Risk** - Risk level before controls
- **Residual Risk** - Risk level after controls

### Treatment
- **Response** - Accept, Mitigate, Transfer, Avoid
- **Controls** - Linked mitigating controls
- **Owner** - Person responsible for the risk

## Risk Matrix

| Impact ↓ / Likelihood → | Rare (1) | Unlikely (2) | Possible (3) | Likely (4) | Almost Certain (5) |
|-------------------------|----------|--------------|--------------|------------|-------------------|
| **Catastrophic (5)** | Medium | Medium | High | Critical | Critical |
| **Major (4)** | Low | Medium | High | High | Critical |
| **Moderate (3)** | Low | Medium | Medium | High | High |
| **Minor (2)** | Low | Low | Medium | Medium | High |
| **Insignificant (1)** | Low | Low | Low | Medium | Medium |

## Dashboard View

The Risks page shows:
- Risk distribution by category
- Top risks by severity
- Risks by treatment status
- Trends over time
    `.trim(),
    tags: ['risks', 'risk-register', 'assessment', 'likelihood', 'impact', 'treatment'],
    relatedPages: ['/risks'],
    icon: 'AlertTriangle',
    order: 1,
  }),

  createArticle({
    id: 'managing-risks',
    title: 'Managing Risks',
    section: 'risks',
    summary: 'Add risks to the register, assess them using likelihood/impact, select treatment options, link controls, and track status over time.',
    content: `
# Managing Risks

Learn how to add, assess, and manage risks in the platform.

## Adding a Risk

1. Go to **Risks**
2. Click **Add Risk**
3. Enter risk details:
   - Title (clear, specific description)
   - Category (security, operational, compliance, etc.)
   - Description (detailed explanation)
4. Click **Create Risk**

## Risk Assessment

### Initial Assessment
After creating a risk, assess it:

1. **Likelihood** - How likely is this risk to occur?
   - 1 = Rare (< once every 5 years)
   - 2 = Unlikely (once per 2-5 years)
   - 3 = Possible (once per 1-2 years)
   - 4 = Likely (multiple times per year)
   - 5 = Almost Certain (frequently/continuously)

2. **Impact** - What's the consequence if it occurs?
   - 1 = Insignificant (minor inconvenience)
   - 2 = Minor (limited damage, easily recovered)
   - 3 = Moderate (significant but manageable)
   - 4 = Major (serious harm, major recovery effort)
   - 5 = Catastrophic (existential threat)

3. **Inherent Risk Score** = Likelihood × Impact

### Residual Assessment
After applying controls:
- Re-assess likelihood and impact
- Calculate residual risk score
- Compare to inherent risk

## Risk Treatment

### Treatment Options

**Accept**
- Risk is within acceptable levels
- Document acceptance decision
- Monitor for changes

**Mitigate**
- Implement controls to reduce risk
- Link mitigating controls
- Track implementation

**Transfer**
- Shift risk to third party
- Insurance or contracts
- Document transfer terms

**Avoid**
- Eliminate the risk entirely
- Change processes or activities
- May not always be possible

## Linking Controls

Connect risks to mitigating controls:
1. Open the risk
2. Click **Link Controls**
3. Search and select controls
4. Controls reduce residual risk

## Risk Reviews

Schedule periodic risk reviews:
- **Quarterly** - Review high/critical risks
- **Annually** - Review all risks
- **Triggered** - Review after incidents or changes

## Best Practices

1. Be specific in risk descriptions
2. Use consistent assessment criteria
3. Document treatment rationale
4. Assign clear ownership
5. Review risks regularly
6. Link to relevant controls
    `.trim(),
    tags: ['risks', 'assessment', 'treatment', 'controls', 'mitigation', 'review'],
    relatedPages: ['/risks'],
    icon: 'ClipboardList',
    order: 2,
  }),
]
